import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import videoServiceInstance from '../../../services/videoService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

const AddVideoModal = ({ isOpen, onClose, onVideoAdded }) => {
  const { userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef();
  const [dragActive, setDragActive] = useState(false);

  // Step data
  const [musicals, setMusicals] = useState([]);
  const [songs, setSongs] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [talents, setTalents] = useState([]);
  
  // Form state - Sequential selection: Musical → Song → Character → Level → Talent
  const [formData, setFormData] = useState({
    musical_id: '',
    song_id: '',
    character_analysis_id: '',
    level: '',
    talent_id: '',
    subsection: ''
  });
  
  const [selectedFile, setSelectedFile] = useState(null);

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadMusicals();
      loadTalents();
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      musical_id: '',
      song_id: '',
      character_analysis_id: '',
      level: '',
      talent_id: '',
      subsection: ''
    });
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
    setSongs([]);
    setCharacters([]);
  };

  const loadMusicals = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('musicals')
        ?.select('id, title')
        ?.eq('is_active', true)
        ?.order('title');
      
      if (error) throw error;
      setMusicals(data || []);
    } catch (error) {
      setError('Failed to load musicals');
      console.error('Error loading musicals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSongsForMusical = async (musicalId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('songs')
        ?.select('id, title, musical_id')
        ?.eq('musical_id', musicalId)
        ?.order('title');
      
      if (error) throw error;
      setSongs(data || []);
    } catch (error) {
      setError('Failed to load songs');
      console.error('Error loading songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCharactersForSong = async (songId) => {
    try {
      setLoading(true);
      // Load character analyses for the specific song - shows Character Roles from musical
      const { data, error } = await supabase
        ?.from('character_song_analyses')
        ?.select(`
          id,
          roles:role_id(
            id,
            character_name,
            musical_id
          )
        `)
        ?.eq('song_id', songId);
      
      if (error) throw error;

      // Sort client-side instead of using server-side ordering to avoid PostgREST parsing errors
      const sortedCharacters = (data || [])?.sort((a, b) => {
        const aName = a?.roles?.character_name || '';
        const bName = b?.roles?.character_name || '';
        return aName?.localeCompare(bName);
      });

      setCharacters(sortedCharacters);
    } catch (error) {
      setError('Failed to load characters');
      console.error('Error loading characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTalents = async () => {
    try {
      const { data, error } = await supabase
        ?.from('talent_profiles')
        ?.select('id, first_name, last_name, display_name')
        ?.order('first_name');
      
      if (error) throw error;
      setTalents(data || []);
    } catch (error) {
      setError('Failed to load talents');
      console.error('Error loading talents:', error);
    }
  };

  // Sequential selection handlers
  const handleMusicalSelect = async (musicalId) => {
    setFormData(prev => ({
      ...prev,
      musical_id: musicalId,
      song_id: '', // Reset subsequent selections
      character_analysis_id: '',
      level: '',
      talent_id: ''
    }));
    
    if (musicalId) {
      await loadSongsForMusical(musicalId);
      setCurrentStep(2); // Move to song selection
    }
    setCharacters([]);
  };

  const handleSongSelect = async (songId) => {
    setFormData(prev => ({
      ...prev,
      song_id: songId,
      character_analysis_id: '', // Reset subsequent selections
      level: '',
      talent_id: ''
    }));
    
    if (songId) {
      await loadCharactersForSong(songId);
      setCurrentStep(3); // Move to character selection
    }
  };

  const handleCharacterSelect = (characterAnalysisId) => {
    setFormData(prev => ({
      ...prev,
      character_analysis_id: characterAnalysisId,
      level: '', // Reset subsequent selections
      talent_id: ''
    }));
    
    if (characterAnalysisId) {
      setCurrentStep(4); // Move to level selection
    }
  };

  const handleLevelSelect = (level) => {
    setFormData(prev => ({
      ...prev,
      level: level,
      talent_id: '' // Reset talent selection
    }));
    
    if (level) {
      setCurrentStep(5); // Move to talent selection
    }
  };

  const handleTalentSelect = (talentId) => {
    setFormData(prev => ({
      ...prev,
      talent_id: talentId
    }));
    
    if (talentId) {
      setCurrentStep(6); // Move to file upload
    }
  };

  // File handling
  const handleFileChange = (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes?.includes(file?.type)) {
      setError('Please select a valid video file (MP4, WebM, OGG, AVI, MOV)');
      return;
    }

    // File size validation (500MB max)
    const maxSize = 500 * 1024 * 1024; // 500MB in bytes
    if (file?.size > maxSize) {
      setError(`File size must be less than 500MB. Current file size: ${formatFileSize(file?.size)}`);
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    handleFileChange(file);
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files?.[0]) {
      handleFileChange(e?.dataTransfer?.files?.[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleUpload = async () => {
    if (!selectedFile || !isFormValid()) {
      setError('Please complete all selections and choose a video file');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Generate file path
      const fileExtension = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
      const timestamp = Date.now();
      const videoPath = `${formData?.song_id}/${formData?.level}/${timestamp}-${selectedFile?.name}`;

      // Progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      // Upload video to storage
      const uploadResult = await videoServiceInstance?.uploadVideo(selectedFile, videoPath);
      
      if (uploadResult?.error) {
        throw new Error(uploadResult?.error?.message || 'Upload failed');
      }

      clearInterval(progressInterval);
      setUploadProgress(95);

      // Create video record in database
      const videoData = {
        video_path: uploadResult?.videoPath,
        thumbnail_path: uploadResult?.thumbnailPath,
        song_id: formData?.song_id,
        character_analysis_id: formData?.character_analysis_id || null,
        level: formData?.level,
        subsection: formData?.subsection || null,
        talent_id: formData?.talent_id || null,
        file_size_bytes: selectedFile?.size || 0,
        duration_seconds: 0 // Will be updated later if needed
      };

      const recordResult = await videoServiceInstance?.createVideoRecord(videoData, userProfile?.id);
      
      if (recordResult?.error) {
        throw new Error(recordResult?.error?.message || 'Failed to create video record');
      }

      setUploadProgress(100);
      
      // Success callback
      if (onVideoAdded) {
        onVideoAdded(recordResult?.data);
      }

      // Close modal after short delay
      setTimeout(() => {
        handleClose();
      }, 1000);

    } catch (err) {
      console.error('Error uploading video:', err);
      setError(err?.message || 'Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isFormValid = () => {
    return formData?.musical_id && 
           formData?.song_id && 
           formData?.level && 
           selectedFile;
  };

  const handleClose = () => {
    if (!uploading) {
      resetForm();
      onClose?.();
    }
  };

  const handleStepBack = () => {
    if (currentStep > 1 && !uploading) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  // Get current selections for display
  const selectedMusical = musicals?.find(m => m?.id === formData?.musical_id);
  const selectedSong = songs?.find(s => s?.id === formData?.song_id);
  const selectedCharacter = characters?.find(c => c?.id === formData?.character_analysis_id);
  const selectedTalent = talents?.find(t => t?.id === formData?.talent_id);

  if (!isOpen) return null;

  const stepTitles = {
    1: 'Select Musical',
    2: 'Select Song',
    3: 'Select Character',
    4: 'Select Level',
    5: 'Select Talent',
    6: 'Upload Video'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative z-[100000]">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="Plus" size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add New Video
                </h3>
                <p className="text-sm text-gray-500">
                  Step {currentStep} of 6: {stepTitles?.[currentStep]}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm" 
              onClick={handleClose}
              disabled={uploading}
              iconName="X"
              className="text-gray-400 hover:text-gray-600"
            />
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
              <span>Progress:</span>
              <span className="font-medium">{Math.round((currentStep / 6) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 6) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Musical Selection */}
          {currentStep === 1 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Choose a Musical</h4>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {musicals?.map((musical) => (
                    <button
                      key={musical?.id}
                      onClick={() => handleMusicalSelect(musical?.id)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <h5 className="font-medium text-gray-900">{musical?.title}</h5>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Song Selection */}
          {currentStep === 2 && (
            <div>
              <div className="mb-4">
                <p className="text-sm text-gray-600">Selected Musical:</p>
                <p className="font-medium text-blue-600">{selectedMusical?.title}</p>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Choose a Song</h4>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {songs?.map((song) => (
                    <button
                      key={song?.id}
                      onClick={() => handleSongSelect(song?.id)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <h5 className="font-medium text-gray-900">{song?.title}</h5>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Character Selection */}
          {currentStep === 3 && (
            <div>
              <div className="mb-4 space-y-1">
                <p className="text-sm text-gray-600">Selected Musical:</p>
                <p className="font-medium text-blue-600">{selectedMusical?.title}</p>
                <p className="text-sm text-gray-600">Selected Song:</p>
                <p className="font-medium text-blue-600">{selectedSong?.title}</p>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Choose Character Role</h4>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : characters?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="User" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No character analyses found for this song.</p>
                  <p className="text-sm text-gray-500">You can skip character selection or go back to choose a different song.</p>
                  <Button
                    variant="outline"
                    onClick={() => handleCharacterSelect('')}
                    className="mt-4"
                  >
                    Skip Character Selection
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {characters?.map((characterAnalysis) => (
                    <button
                      key={characterAnalysis?.id}
                      onClick={() => handleCharacterSelect(characterAnalysis?.id)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <h5 className="font-medium text-gray-900">{characterAnalysis?.roles?.character_name}</h5>
                      <p className="text-sm text-gray-600">Character Role from Musical</p>
                    </button>
                  ))}
                  <button
                    onClick={() => handleCharacterSelect('')}
                    className="p-4 border border-gray-300 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <h5 className="font-medium text-gray-600">No Character</h5>
                    <p className="text-sm text-gray-500">Skip character selection</p>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Level Selection */}
          {currentStep === 4 && (
            <div>
              <div className="mb-4 space-y-1">
                <p className="text-sm text-gray-600">Selected Musical:</p>
                <p className="font-medium text-blue-600">{selectedMusical?.title}</p>
                <p className="text-sm text-gray-600">Selected Song:</p>
                <p className="font-medium text-blue-600">{selectedSong?.title}</p>
                {selectedCharacter && (
                  <>
                    <p className="text-sm text-gray-600">Selected Character:</p>
                    <p className="font-medium text-blue-600">{selectedCharacter?.roles?.character_name}</p>
                  </>
                )}
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Choose Level</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: 'overview', label: 'Overview', description: 'General overview' },
                  { value: 'level1', label: 'Level 1', description: 'Basic analysis' },
                  { value: 'level2', label: 'Level 2', description: 'Intermediate analysis' },
                  { value: 'level3', label: 'Level 3', description: 'Advanced analysis' },
                  { value: 'level4', label: 'Level 4', description: 'Expert analysis' },
                  { value: 'level5', label: 'Level 5', description: 'Master level' },
                  { value: 'phrases', label: 'Phrases', description: 'Phrase breakdown' }
                ]?.map((level) => (
                  <button
                    key={level?.value}
                    onClick={() => handleLevelSelect(level?.value)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-center"
                  >
                    <h5 className="font-medium text-gray-900">{level?.label}</h5>
                    <p className="text-xs text-gray-600">{level?.description}</p>
                  </button>
                ))}
              </div>
              
              {/* Optional Subsection */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subsection (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., opening, chorus, bridge..."
                  value={formData?.subsection}
                  onChange={(e) => setFormData(prev => ({ ...prev, subsection: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Step 5: Talent Selection */}
          {currentStep === 5 && (
            <div>
              <div className="mb-4 space-y-1">
                <p className="text-sm text-gray-600">Level:</p>
                <p className="font-medium text-blue-600">{formData?.level?.charAt(0)?.toUpperCase() + formData?.level?.slice(1)}</p>
                {formData?.subsection && (
                  <>
                    <p className="text-sm text-gray-600">Subsection:</p>
                    <p className="font-medium text-blue-600">{formData?.subsection}</p>
                  </>
                )}
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Choose Talent</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {talents?.map((talent) => (
                  <button
                    key={talent?.id}
                    onClick={() => handleTalentSelect(talent?.id)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <h5 className="font-medium text-gray-900">
                      {talent?.display_name || `${talent?.first_name} ${talent?.last_name}`}
                    </h5>
                  </button>
                ))}
                <button
                  onClick={() => handleTalentSelect('')}
                  className="p-4 border border-gray-300 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <h5 className="font-medium text-gray-600">No Talent</h5>
                  <p className="text-sm text-gray-500">Skip talent selection</p>
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Video Upload */}
          {currentStep === 6 && (
            <div>
              {/* Selection Summary */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-2">Video Details</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Musical:</span>
                    <p className="font-medium">{selectedMusical?.title}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Song:</span>
                    <p className="font-medium">{selectedSong?.title}</p>
                  </div>
                  {selectedCharacter && (
                    <div>
                      <span className="text-gray-600">Character:</span>
                      <p className="font-medium">{selectedCharacter?.roles?.character_name}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Level:</span>
                    <p className="font-medium">{formData?.level?.charAt(0)?.toUpperCase() + formData?.level?.slice(1)}</p>
                  </div>
                  {formData?.subsection && (
                    <div>
                      <span className="text-gray-600">Subsection:</span>
                      <p className="font-medium">{formData?.subsection}</p>
                    </div>
                  )}
                  {selectedTalent && (
                    <div>
                      <span className="text-gray-600">Talent:</span>
                      <p className="font-medium">{selectedTalent?.display_name || `${selectedTalent?.first_name} ${selectedTalent?.last_name}`}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Video File
                </label>
                
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-500 bg-blue-50' 
                      : selectedFile 
                        ? 'border-green-300 bg-green-50' :'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef?.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                  
                  {selectedFile ? (
                    <div className="flex items-center justify-center space-x-3">
                      <Icon name="Video" size={24} className="text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          {selectedFile?.name}
                        </p>
                        <p className="text-xs text-green-600">
                          {formatFileSize(selectedFile?.size)} • Ready to upload
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Icon name="Upload" size={32} className="text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">
                        Drop your video file here or click to browse
                      </p>
                      <p className="text-xs text-gray-500">
                        Supported formats: MP4, WebM, OGG, AVI, MOV (max 500MB)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Uploading... {Math.round(uploadProgress)}%
                    </span>
                    <LoadingSpinner size="sm" />
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={16} className="text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Error</p>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={handleStepBack}
                  disabled={uploading}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back
                </Button>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="danger"
                onClick={handleClose}
                disabled={uploading}
              >
                Cancel
              </Button>
              
              {currentStep === 6 && (
                <Button
                  variant="default"
                  onClick={handleUpload}
                  disabled={!isFormValid() || uploading}
                  iconName={uploading ? null : "Upload"}
                  iconPosition="left"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Uploading...</span>
                    </>
                  ) : (
                    'Upload Video'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVideoModal;